import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router";
import _ from "lodash";

import {
  AssistantOutlinedIcon,
  AutoAwesomeOutlinedIcon,
  CloudUploadIcon,
} from "icons";
import theme from "theme";
import { colors } from "theme/palette";
import { useCaching } from "utils/common";
import { format, FORMATS, parseISO } from "utils/datetime";

import Button from "components/Button";
import Dialog, { DialogTitle } from "components/Dialog";
import { EMPTY_VALUE_REGEX } from "components/RichTextEditor/constants";
import RichTextEditorField from "components/RichTextEditorField";
import SmallAlert from "components/SmallAlert";
import Stack from "components/Stack";
import Text from "components/Text";
import toast from "components/Toast";
import Tooltip from "components/Tooltip";
import { useAIMemoSection } from "components/useAIMemoSection";
import { useConfirmationDialog } from "components/useConfirmationDialog";

import { ECompanySettingsFeatureType } from "entities/Company/components/CompanySettings/constants";
import { useCheckFeature } from "entities/Company/components/CompanySettings/utils";
import { fieldValueCreate } from "entities/Field/sdk";
import { handleInvalidMemoFieldRequest } from "entities/Memo/components/Fields/utils/utils";
import {
  AIPromptSection,
  memoSectionAIProcessTrigger,
} from "entities/Memo/sdk";
import { useMemoSectionAIExtractionProcessing } from "entities/TableStorage/hooks";

export interface IAIAnswerSectionEditDialog {
  name: string;
  onUpdate: ({ message }: { message?: string }) => void;
  memoSectionId: number;
  isOpen: boolean;
  onClose: () => void;
  aiPromptSection: AIPromptSection;
  fieldId: number;
  fieldValue: string | null;
  lastSavedAt: string | null;
  isPromptResponseInProgress: boolean;
}

const AIAnswerSectionEditDialog = ({
  name,
  memoSectionId,
  isOpen,
  onUpdate,
  onClose,
  fieldValue,
  fieldId,
  aiPromptSection,
  lastSavedAt,
  isPromptResponseInProgress,
}: IAIAnswerSectionEditDialog) => {
  const [disableGenerateButtonOnClick, setDisableGenerateButtonOnClick] =
    useState<boolean>(false);
  const [saveTimer, setSaveTimer] = useState<NodeJS.Timeout | null>(null);

  const params = useParams<{
    memoId: string;
    dealId: string;
  }>();

  const memoId = useMemo(() => Number(params.memoId), [params.memoId]);
  const dealId = useMemo(() => Number(params.dealId), [params.dealId]);

  const navigate = useNavigate();

  const { handleSelectedSourceDocumentId, isSourceDocumentDrawerOpen } =
    useAIMemoSection();

  const { show: showConfirmationDialog } = useConfirmationDialog();

  const disableGenerateAnswersButton = useMemo(() => {
    return (
      aiPromptSection.source_documents?.some(
        (document) => document.is_missing || document.is_na
      ) ||
      isPromptResponseInProgress ||
      disableGenerateButtonOnClick
    );
  }, [
    aiPromptSection.source_documents,
    isPromptResponseInProgress,
    disableGenerateButtonOnClick,
  ]);

  const generateAnswerButtonTooltipText = useMemo(() => {
    if (
      aiPromptSection.source_documents?.some(
        (document) => document.is_missing || document.is_na
      )
    ) {
      return "You must provide all AI source documents in order to generate an answer.";
    }

    if (isPromptResponseInProgress || disableGenerateButtonOnClick) {
      return "Generating...";
    }
  }, [
    aiPromptSection.source_documents,
    isPromptResponseInProgress,
    disableGenerateButtonOnClick,
  ]);

  const [value, setValue] = useCaching<string | null>(fieldValue);

  useEffect(() => {
    if (!isOpen) {
      setValue(fieldValue);
    }
  }, [isOpen, fieldValue, setValue]);

  const hasValue = useMemo(
    () =>
      !_.isNil(value) && !_.isEmpty(value) && !EMPTY_VALUE_REGEX.test(value),
    [value]
  );

  const onSave = useCallback(
    ({ newValue }: { newValue: string | null }) => {
      setValue(newValue);
      fieldValueCreate({
        dealId,
        companyCustomFieldId: fieldId,
        value: newValue,
      })
        .then(() => {
          if (!isSourceDocumentDrawerOpen) {
            handleSelectedSourceDocumentId({ value: null });
          }
        })
        .catch((error) =>
          handleInvalidMemoFieldRequest({ error, dealId, navigate })
        );
    },
    [
      fieldId,
      dealId,
      setValue,
      navigate,
      handleSelectedSourceDocumentId,
      isSourceDocumentDrawerOpen,
    ]
  );

  const handleSave = useCallback(() => {
    const validatedValue = EMPTY_VALUE_REGEX.test(value || "") ? "" : value;
    onSave({ newValue: validatedValue });
    onClose();
    onUpdate({ message: `${name} successfully updated!` });
  }, [onClose, onUpdate, onSave, value, name]);

  const handleChange = useCallback(
    ({ newValue }: { newValue: string }) => {
      setValue(newValue); // Save the data every 3 seconds of inactivity.
      const newTimer = setTimeout(() => {
        const validatedValue = EMPTY_VALUE_REGEX.test(newValue || "")
          ? ""
          : newValue;
        onSave({ newValue: validatedValue });
        onUpdate({});
      }, 3000);

      setSaveTimer((prevTimer) => {
        if (!_.isNull(prevTimer)) {
          clearTimeout(prevTimer);
        }

        return newTimer;
      });
    },
    [onSave, onUpdate, setValue]
  );

  const handleClose = useCallback(() => {
    if (!_.isNull(saveTimer)) {
      clearTimeout(saveTimer);
    }

    onClose();
    onUpdate({});
    if (!isSourceDocumentDrawerOpen) {
      handleSelectedSourceDocumentId({ value: null });
    }
  }, [
    onUpdate,
    onClose,
    handleSelectedSourceDocumentId,
    isSourceDocumentDrawerOpen,
    saveTimer,
  ]);

  const handleMemoSectionAIExtractionTrigger = useCallback(() => {
    if (hasValue) {
      showConfirmationDialog({
        onConfirm: () => {
          setDisableGenerateButtonOnClick(true);
          memoSectionAIProcessTrigger({
            memoId,
            dealId,
            memoSectionIds: [memoSectionId],
          })
            .then(() => {
              onUpdate({});
              handleClose();
              toast.successMessage(
                "The AI Extraction has started, you will be notified when that data has been processed!"
              );
            })
            .catch(handleInvalidMemoFieldRequest);
        },
        confirmButtonText: "Generate answer",
        confirmButtonColor: "primary",
        message: (
          <Text fontWeight={500} fontSize={18}>
            Generating a new answer will overwrite your current edits. Are you
            sure you want to continue?
          </Text>
        ),
      });
    } else {
      setDisableGenerateButtonOnClick(true);
      memoSectionAIProcessTrigger({
        memoId,
        dealId,
        memoSectionIds: [memoSectionId],
      })
        .then(() => {
          onUpdate({});
          handleClose();
          toast.successMessage(
            "The AI Extraction has started, you will be notified when that data has been processed!"
          );
        })
        .catch(handleInvalidMemoFieldRequest);
    }
  }, [
    memoId,
    dealId,
    memoSectionId,
    handleClose,
    onUpdate,
    showConfirmationDialog,
    hasValue,
  ]);

  const onSourceDocumentIconClick = useCallback(
    ({ document_id }: { document_id: number }) => {
      handleClose();
      handleSelectedSourceDocumentId({
        value: document_id,
      });
    },
    [handleSelectedSourceDocumentId, handleClose]
  );

  const handleSourceDocumentIconClick = useCheckFeature({
    feature: ECompanySettingsFeatureType.ONBOARDING_COMPANY_PACKAGE,
    callback: ({ document_id }: { document_id: number }) =>
      onSourceDocumentIconClick({ document_id }),
  });

  const handleGenerateNewAIAnswer = useCheckFeature({
    feature: ECompanySettingsFeatureType.ONBOARDING_COMPANY_PACKAGE,
    callback: handleMemoSectionAIExtractionTrigger,
  });

  const extractionProcessFinishHandler = useCallback(() => {
    if (!isSourceDocumentDrawerOpen) {
      handleSelectedSourceDocumentId({ value: null });
    }
    setDisableGenerateButtonOnClick(false);
  }, [handleSelectedSourceDocumentId, isSourceDocumentDrawerOpen]);

  useMemoSectionAIExtractionProcessing({
    successEventHandler: extractionProcessFinishHandler,
    failureEventHandler: extractionProcessFinishHandler,
  });

  return (
    <Dialog
      open={isOpen}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: { padding: 1 },
      }}
      onClose={handleClose}
    >
      <DialogTitle
        data-testid="edit-dialog-title"
        sx={{
          margin: theme.spacing(2, 2, 0, 2),
          padding: 2,
          borderRadius: "8px",
          backgroundColor: colors.blue10,
        }}
      >
        <Stack direction="row" alignItems="center" gap={1.5}>
          <AssistantOutlinedIcon sx={{ width: "24px", height: "24px" }} />
          <Text variant="h2">{name}</Text>
        </Stack>
      </DialogTitle>
      <Stack gap={1} padding={theme.spacing(2, 2, 0, 2)}>
        {!_.isEmpty(aiPromptSection.source_documents) && (
          <Text variant="labelSmall">AI SOURCE DOCUMENTS USED</Text>
        )}
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <Stack
            direction="row"
            gap={1.5}
            alignItems="center"
            flexWrap="wrap"
            alignSelf="center"
          >
            {aiPromptSection.source_documents?.map((document) => (
              <Tooltip
                title={
                  document.is_missing || document.is_na
                    ? "Document is missing or no document has been selected as an AI source. Click here to resolve."
                    : "View source documents"
                }
                key={document.name}
              >
                <Stack
                  padding={theme.spacing(0.5, 1.5, 0.5, 1)}
                  border={`1px solid ${colors.gray40}`}
                  borderRadius={5}
                  direction="row"
                  gap={1}
                  alignItems="center"
                  width="fit-content"
                  onClick={() =>
                    handleSourceDocumentIconClick({
                      document_id: document.id,
                    })
                  }
                  sx={{
                    cursor: "pointer",
                  }}
                >
                  {document.is_missing || document.is_na ? (
                    <CloudUploadIcon
                      sx={{
                        color: colors.red100,
                        width: 16,
                        height: 16,
                      }}
                    />
                  ) : (
                    <AutoAwesomeOutlinedIcon
                      sx={{ width: 16, height: 16, color: colors.gray60 }}
                    />
                  )}
                  <Text fontWeight={500} variant="text4" noWrap>
                    {document.name}
                  </Text>
                </Stack>
              </Tooltip>
            ))}
          </Stack>
          <Tooltip title={generateAnswerButtonTooltipText}>
            <Stack
              alignSelf="flex-start"
              sx={{
                cursor: disableGenerateAnswersButton
                  ? "not-allowed"
                  : "pointer",
              }}
            >
              <Button
                onClick={handleGenerateNewAIAnswer}
                disabled={disableGenerateAnswersButton}
                variant="outlined"
                fullWidth
                sx={{ whiteSpace: "nowrap" }}
              >
                Generate new AI answer
              </Button>
            </Stack>
          </Tooltip>
        </Stack>
      </Stack>

      <RichTextEditorField
        defaultValue={!_.isNil(value) ? value : ""}
        onChange={(value) => handleChange({ newValue: value })}
        sx={{ padding: theme.spacing(2, 2, 0, 2), overflow: "hidden" }}
      />

      <Stack
        sx={{
          flexDirection: "row",
          padding: 2,
        }}
        alignItems="center"
        justifyContent="space-between"
      >
        <Stack direction="row" spacing={1} alignItems="center">
          <SmallAlert title="Don’t forget to save your changes after editing!" />
          {!_.isNil(lastSavedAt) && (
            <Text
              variant="text3"
              component="span"
              sx={{ color: colors.gray80 }}
              data-testid="last-saved-timestamp"
            >
              Last saved at{" "}
              {format(parseISO(lastSavedAt), FORMATS.FRONTEND.DATETIME)}
            </Text>
          )}
        </Stack>
        <Stack direction="row" spacing={1}>
          <Button
            variant="text"
            onClick={handleClose}
            data-testid="cancel-button"
          >
            Cancel
          </Button>
          <Button onClick={handleSave} data-testid="submit-button">
            Save changes
          </Button>
        </Stack>
      </Stack>
    </Dialog>
  );
};

export default AIAnswerSectionEditDialog;
